import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap
} from 'rxjs/operators';
import { Logger } from '@app/utils/logger';
import { EVENT } from '@providers/event/event.enum';
import { EventService } from '@providers/event/event.service';
import { Session } from '@providers/session/session.service';
import { AvatarCacheService } from '../services/avatar-cache.service';
import * as AvatarDisplayActions from './avatar-display.actions';
import { AvatarDisplayService } from './avatar-display.service';

/**
 * Avatar Display Effects
 * NgRx effects for avatar display functionality.
 */
@Injectable()
export class AvatarDisplayEffects {
  private readonly actions$ = inject(Actions);
  private readonly avatarService = inject(AvatarDisplayService);
  private readonly cacheService = inject(AvatarCacheService);
  private readonly eventService = inject(EventService);
  private readonly session = inject(Session);

  private readonly log = Logger.getInstance('[AvatarDisplayEffects]');

  /**
   * Effect: Auto-fetch avatar image after successful status check
   * Automatically fetches the avatar image when status check succeeds and current avatar exists.
   */
  autoFetchAvatarImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarDisplayActions.checkAvatarStatusSuccess),
      filter(({ status }) => !!status.current?.hasImages),
      map(({ profileId }) => {
        this.log.log(
          '[AvatarDisplayEffects] Auto-fetching avatar image for profile:',
          profileId
        );
        return AvatarDisplayActions.fetchAvatarImage({
          profileId,
          imageType: 'HOMEPAGE' // Default to homepage for now
        });
      })
    )
  );

  /**
   * Effect: Check avatar status
   * Fetches avatar status and automatically triggers image fetch if status is COMPLETED.
   */
  checkAvatarStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarDisplayActions.checkAvatarStatus),
      switchMap(({ profileId }) =>
        this.avatarService.getAvatarStatus(profileId).pipe(
          map((response) => {
            this.log.log(
              '[AvatarDisplayEffects] Avatar status fetched:',
              response
            );

            // Dispatch success action with profileId
            return AvatarDisplayActions.checkAvatarStatusSuccess({
              profileId,
              status: response.data
            });
          }),
          catchError((error) => {
            this.log.error(
              '[AvatarDisplayEffects] Error checking avatar status:',
              error
            );
            return of(
              AvatarDisplayActions.checkAvatarStatusFailure({
                error: error.message ?? 'Failed to check avatar status'
              })
            );
          })
        )
      )
    )
  );

  /**
   * Effect: Fetch avatar image
   * Checks cache first, then fetches from API if not cached.
   */
  fetchAvatarImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarDisplayActions.fetchAvatarImage),
      mergeMap(({ profileId, imageType }) => {
        // Check cache first
        const cachedUrl = this.cacheService.get(profileId, imageType);

        if (cachedUrl) {
          this.log.log(
            '[AvatarDisplayEffects] Avatar image found in cache:',
            cachedUrl
          );
          return of({
            fromCache: true,
            profileId,
            imageType,
            imageUrl: cachedUrl
          });
        }

        this.log.log(
          '[AvatarDisplayEffects] Avatar image not in cache, fetching from API'
        );
        return of({
          fromCache: false,
          profileId,
          imageType,
          imageUrl: null
        });
      }),
      switchMap(({ fromCache, profileId, imageType, imageUrl }) => {
        if (fromCache && imageUrl) {
          // Return cached image
          return of(
            AvatarDisplayActions.fetchAvatarImageSuccess({
              image: {
                imageUrl,
                isDefault: false // Cached images are always custom
              }
            })
          );
        }

        // Fetch from API
        return this.avatarService.getAvatarImage(profileId, imageType).pipe(
          map((response) => {
            this.log.log(
              '[AvatarDisplayEffects] Avatar image fetched from API:',
              response
            );

            // Cache the image URL if it's not a default
            if (!response.data.isDefault) {
              this.cacheService.set(
                profileId,
                imageType,
                response.data.imageUrl
              );
            }

            return AvatarDisplayActions.fetchAvatarImageSuccess({
              image: response.data
            });
          }),
          catchError((error) => {
            this.log.error(
              '[AvatarDisplayEffects] Error fetching avatar image:',
              error
            );
            return of(
              AvatarDisplayActions.fetchAvatarImageFailure({
                error: error.message ?? 'Failed to fetch avatar image'
              })
            );
          })
        );
      })
    )
  );

  /**
   * Effect: Invalidate avatar cache
   * Clears cache when explicitly requested.
   */
  invalidateAvatarCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AvatarDisplayActions.invalidateAvatarCache),
        tap(({ profileId, imageType }) => {
          this.log.log('[AvatarDisplayEffects] Invalidating avatar cache:', {
            profileId,
            imageType
          });
          this.cacheService.invalidate(profileId, imageType);
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect: Handle authentication changes
   * Clear state on logout, refresh on login/profile switch.
   */
  onAuthChange$ = createEffect(() =>
    this.eventService.listen(EVENT.LOGIN).pipe(
      tap(() => {
        this.log.log('[AvatarDisplayEffects] Login event detected');
      }),
      switchMap(() => {
        const profile = this.session.getProfile();
        if (profile?.id) {
          return of(
            AvatarDisplayActions.checkAvatarStatus({ profileId: profile.id })
          );
        }
        return EMPTY;
      })
    )
  );

  /**
   * Effect: Handle OneSignal push notifications for avatar events
   * Listens to AVATAR_NOTIFICATION event and triggers appropriate actions.
   */
  onAvatarNotificationReceived$ = createEffect(() =>
    this.eventService.listen(EVENT.AVATAR_NOTIFICATION).pipe(
      filter((notification) => {
        // Filter for avatar-related notifications
        return ['AVATAR_COMPLETED', 'AVATAR_FAILED'].includes(
          notification.type
        );
      }),
      tap((notification) => {
        this.log.log(
          '[AvatarDisplayEffects] Avatar notification received:',
          notification
        );
      }),
      switchMap((notification) => {
        const profileId =
          notification.profileId ?? this.session.getProfile()?.id;

        if (!profileId) {
          this.log.warn(
            '[AvatarDisplayEffects] No profile ID available for notification'
          );
          return EMPTY;
        }

        // Invalidate cache first
        return of(profileId).pipe(
          tap((id) => {
            this.log.log(
              '[AvatarDisplayEffects] Invalidating cache for profile:',
              id
            );
            this.cacheService.invalidate(id);
          }),
          switchMap((id) =>
            of(
              AvatarDisplayActions.refreshAvatarOnNotification({
                notificationType: notification.type,
                profileId: id
              })
            )
          )
        );
      })
    )
  );

  /**
   * Effect: Handle logout
   * Clears all avatar caches from localStorage and resets state.
   */
  onLogout$ = createEffect(() =>
    this.eventService.listen(EVENT.LOGOUT).pipe(
      tap(() => {
        this.log.log('[AvatarDisplayEffects] Logout event detected');
        this.log.log('[AvatarDisplayEffects] Clearing all avatar caches');
        // Clear all avatar caches from localStorage
        this.cacheService.clear();
      }),
      map(() => AvatarDisplayActions.clearAvatarState())
    )
  );

  /**
   * Effect: Handle profile switch
   * Invalidates old profile cache and clears state (components will refetch when needed).
   */
  onProfileSwitch$ = createEffect(() =>
    this.eventService.listen(EVENT.PROFILE_SWITCHED).pipe(
      tap((event) => {
        this.log.log('[AvatarDisplayEffects] Profile switch event detected');
        // Invalidate OLD profile cache if provided
        if (event?.oldProfileId) {
          this.log.log(
            '[AvatarDisplayEffects] Invalidating cache for old profile:',
            event.oldProfileId
          );
          this.cacheService.invalidate(event.oldProfileId);
        }
      }),
      // DON'T fetch here - clear state and let components refetch when needed
      // This prevents race condition where fetch happens with stale profile ID
      map(() => AvatarDisplayActions.clearAvatarState())
    )
  );

  /**
   * Effect: Handle profile update
   * Invalidates default avatar cache when profile is updated (e.g., gender change).
   */
  onProfileUpdate$ = createEffect(() =>
    this.eventService.listen(EVENT.PROFILE_UPDATED).pipe(
      tap(() => {
        this.log.log('[AvatarDisplayEffects] Profile update event detected');
      }),
      switchMap((event) => {
        const profileId = event?.profileId ?? this.session.getProfile()?.id;
        if (profileId) {
          this.log.log(
            '[AvatarDisplayEffects] Invalidating default avatar cache for profile:',
            profileId
          );
          // Invalidate only HOMEPAGE (default avatar) cache
          this.cacheService.invalidate(profileId, 'HOMEPAGE');
          return of(AvatarDisplayActions.checkAvatarStatus({ profileId }));
        }
        return EMPTY;
      })
    )
  );

  /**
   * Effect: Refresh avatar after notification
   * Triggers avatar status check after receiving a notification.
   */
  refreshAvatarAfterNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarDisplayActions.refreshAvatarOnNotification),
      map(({ profileId }) => {
        this.log.log(
          '[AvatarDisplayEffects] Refreshing avatar status after notification'
        );
        return AvatarDisplayActions.checkAvatarStatus({ profileId });
      })
    )
  );
}
